// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ModalType} from 'common/components/modal';
import {capitalize} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {FeatureGate, ProjectFeatureFlag, ReportName, RestrictEntitySaving} from 'common/report/constants';
import {getReportUrl} from 'common/report/util';
import {cohortsStore} from 'common/report/stores/cohorts';
import {reporter} from 'common/report/error-reporting';
import {
  buildCreateEntityModalAttrs,
  isSharingEnabledForProject,
  normalizeCohortEntity,
} from 'common/report/util/saved-entities';
import {cohortsClient} from 'common/report/queries/app-api/cohorts-client';
import {fetchPeopleCountByCohort} from 'common/report/queries/query-api/people-stats-client';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {userDetailsStore} from 'common/report/stores/user-details';
import {SerializedCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {CohortsQueryBuilderUpdate} from 'common/query-builder/cohorts-query-builder/types';
import {IronContextRegistry} from 'common/report/context-registry';

import {promptSharing} from '../sharing';
import {getUpsellInfo, ModalTrigger} from '../in-product-upsell-modal/util';
import {template} from './template';
import {
  CohortsModalError,
  CohortsModalState,
  InlineCohortsModalAttrsJsxProps,
  InlineCohortsModalCommitEvent,
} from './types';

export class InlineCohortsModal extends Component<
  CohortsModalState,
  InlineCohortsModalAttrsJsxProps[`attrs`],
  unknown,
  unknown,
  IronContextRegistry
> {
  public declare context;
  private declare _cohortStoreCallback;
  private currentProjectStoreCallback = () => this.update();

  static get attrsSchema(): AttrsSchema<InlineCohortsModalAttrsJsxProps[`attrs`]> {
    return {
      ...super.attrsSchema,
      cohort: `json`,
      'project-id': `number`,
      'workspace-id': `number`,
      'data-group-id': `string`,
      'can-save': `boolean`,
      'can-apply-unsaved-cohort': `boolean`,
      'project-has-cohorts': `boolean`,
      'support-b2b-cohorts': `boolean`,
      'user-id': `string`,
      'show-cohort-count': `boolean`,
      'track-name': `string`,
      'can-edit': `boolean`,
      name: `string`,
      open: `boolean`,
      type: `string`,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchCohorts();
    this.fetchCohortCount();

    // update app when user details are updated (from backend or otherwise)
    userDetailsStore.subscribe(() => this.update()).fetch();
    currentProjectStore.subscribe(this.currentProjectStoreCallback).fetch();
  }

  disconnectedCallback() {
    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    cohortsStore.unsubscribe(this._cohortStoreCallback);
    super.disconnectedCallback();
  }

  hasSharingFeatureFlag() {
    return isSharingEnabledForProject(currentProjectStore.value);
  }

  fetchCohorts() {
    this.update({loadingCohorts: true});

    this._cohortStoreCallback = () => {
      this.update({loadingCohorts: false});
    };

    cohortsStore.subscribe(this._cohortStoreCallback);

    cohortsStore.fetch({
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);

    switch (attr) {
      case `open`:
      case `name`:
        this.update({name: this.attr(`name`)});
        break;
      case `cohort`: {
        const cohort = Cohort.parseCohort(this.attr(`cohort`));
        this.update({
          cohort,
        });
        break;
      }
    }
  }

  get config() {
    return {
      template,
      contexts: [`i18n` as const, `currentProject` as const],
      defaultState: {
        name: ``,
        showQueryBuilderSkeleton: true,
        showCreateEntityModal: false,
        loadingCohorts: false,
        loadingCohortCount: false,
        errors: new Set<CohortsModalError>(),
        cohortCount: 0,
        cohortSaveAttempt: null,
        cohort: new Cohort(),
        samplingFactor: 1.0,
        saveCohortAttempted: false,
      },
      helpers: {
        CohortsModalError,
        isNewCohort: () => {
          const {unsavedId, id} = this.state.cohort;
          return id ? false : !unsavedId;
        },
        isNewUnsavedCohort: () => !this.state.cohort.unsavedId,
        isNewSavedCohort: () => !this.state.cohort.id,
        trackCohortsBuilderEvent: ({eventName, props}) => {
          this.trackEvent(eventName, props);
        },
        getBannerText: () =>
          `${this.t(`users.cohorts.error.no-permission`)} ${
            !this.helpers.isEditableCohort() ? this.t(`users.cohorts.error.try-save-as-new`) : ``
          }`,
        getMPContext: () => this.context,
        getCohorts: () =>
          Object.values(cohortsStore.value).map((cohort: SerializedCohort) => ({
            createdBy: cohort.created_by,
            lastEdited: cohort.last_edited,
            ...cohort,
          })),
        getCohortNames: () =>
          this.helpers
            .getCohorts()
            .filter((cohort) => Number.isInteger(cohort.id))
            .map(({name}) => name.toLowerCase()),
        getCohortNameError: () => this.getErrorCopy(),

        getTitle: () => {
          // Only display title if Modal is open
          if (!this.attr(`open`)) {
            return ``;
          }

          if (!this.helpers.isNewSavedCohort() && !this.attr(`can-edit`)) {
            return this.state.cohort.name;
          }
          const isNewCohort = this.helpers.isNewCohort();

          return isNewCohort ? this.t(`users.cohorts.create-title`) : this.t(`users.cohorts.edit-title`);
        },

        isEditableCohort: (): boolean => {
          return this.state.cohort.canExpandCohortDefinition();
        },

        getDisabledEditingReason: (): string => {
          // Attribution Cohorts can contain NthTimeInPeriodFilters so check this first
          // for a more accurate copy message.
          if (this.state.cohort.isAttributionCohort()) {
            return this.t(`users.cohorts.error.cant-edit-attribution`);
          }
          if (this.state.cohort.hasNthTimeInPeriodFilter()) {
            return this.t(`users.cohorts.error.cant-edit-period-filters`);
          }
          if (this.state.cohort.isReportBased()) {
            const reportName: string = String(this.state.cohort.reportName).slice();
            return this.t(`users.cohorts.error.cant-edit-report`, {reportName: capitalize(reportName)});
          } else {
            return ``;
          }
        },

        displayCohortCount: () => {
          return this.t(`users.cohorts.user-count`, {number: this.state.cohortCount.toLocaleString()});
        },

        handleCohortUpdated: ({cohort}: CohortsQueryBuilderUpdate) => {
          this.update({
            cohort: Cohort.parseCohort({...cohort.serialize(), name: this.state.name}),
          });
          this.fetchCohortCount();
        },
        isSavingRestricted: () => !currentProjectStore.value?.canSaveEntity(RestrictEntitySaving.Cohorts),
        openPricingUpsell: () => {
          this.trackEvent(`Click Upsell`, {
            'Feature Limit': FeatureGate.RestrictEntitySaving,
            'Entity Type': `cohorts`,
          });
          window.open(`https://mixpanel.com/pricing/`, `_blank`).focus();
        },
        isOpen: () => this.attr(`open`),
        saveStagedCohortOnInlineModal: async (isDuplicate = false) => {
          if (this.state.name.trim().length === 0) {
            return this.addError(CohortsModalError.NameRequired);
          } else if (this.hasExistingName()) {
            return this.addError(CohortsModalError.ExistingName);
          }

          if (isDuplicate) {
            this.update({showCreateEntityModal: true});
          } else {
            await this.saveCohort({name: this.state.name});
          }
        },
        closeEntityModal: () => this.update({showCreateEntityModal: false}),
        openCohortViewUser: () => {
          const {cohort} = this.state;
          const userViewUrl =
            getReportUrl(ReportName.Users, {
              projectId: this.attr(`project-id`),
              workspaceId: this.attr(`workspace-id`),
            }) +
            Cohort.getExploreHashForUsersInCohort({
              cohortId: cohort.id,
              cohortName: cohort.name,
              dataGroupId: cohort.dataGroupId,
              ...(Number.isInteger(cohort.id) ? {} : {serializedCohort: cohort.serialize()}),
            });
          window.open(userViewUrl);
        },
        commitCohort: () => {
          if (this.state.name.trim().length === 0) {
            return this.addError(CohortsModalError.NameRequired);
          }

          const {cohort} = this.state;
          this.dispatchEvent(
            new CustomEvent(`commitCohort`, {
              detail: {name: cohort.name, cohort},
            }) as InlineCohortsModalCommitEvent,
          );
        },
        saveStagedCohortOnCreateModal: async (ev) => {
          try {
            const savedCohort = await this.saveCohort(ev.detail.value, true);

            const {id} = savedCohort;
            // When Save and Share button is clicked, open a sharing modal
            if (ev.type === `saveAndShare`) {
              await promptSharing({
                type: SharableEntityType.Cohort,
                id,
                'project-id': this.attr(`project-id`),
              });
            }
          } catch (error) {
            this.helpers.closeModal();
            reporter.error(error);
          }
          this.helpers.closeEntityModal();
        },
        getModalAttributes: () => {
          return {
            open: this.attr(`open`),
            closeable: true,
            'grow-to-content': true,
            'modal-type': ModalType.Modal,
            ...(this.hasUnsavedChanges() ? {'close-confirm-text': this.t(`users.cohorts.confirm-unsaved`)} : {}),
          };
        },
        handleNameInputChange: (ev) => {
          const {value} = ev.target;
          if (value && this.state.errors.has(CohortsModalError.NameRequired)) {
            this.removeError(CohortsModalError.NameRequired);
          }
          if (value !== this.state.name && this.state.errors.has(CohortsModalError.ExistingName)) {
            this.removeError(CohortsModalError.ExistingName);
          }

          this.update({
            name: value,
            cohort: Cohort.parseCohort({...this.state.cohort.serialize(), name: value}),
          });
        },
        handleModalStateChange: (ev) => {
          const {state} = ev.detail;

          if (state === `closed`) {
            return this.helpers.closeModal();
          }

          this.dispatchEvent(new CustomEvent(`change`, {detail: {state}}));
        },
        closeModal: () => {
          this.update({errors: new Set()});
          this.dispatchEvent(new CustomEvent(`close`, {detail: {state: `closed`}}));
        },
        buildCreateEntityModalAttrs: () => {
          const {cohort} = this.state;
          return buildCreateEntityModalAttrs({
            projectId: this.attr(`project-id`),
            entityType: EntityType.Cohort,
            modalOpen: true,
            isEditing: false,
            isDuplicating: true,
            entity: normalizeCohortEntity(cohort),
            showShareManagement: this.hasSharingFeatureFlag(),
            i18n: this.getContext(`i18n`),
          });
        },
        shouldRenderUpsellModal: () => {
          return getUpsellInfo(this.getContext(`currentProject`)?.get()?.value, ModalTrigger.COHORT);
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  /**
   * Saves or creates a cohort
   */
  async saveCohort(saveParams, isDuplicate = false) {
    let savedCohort: SerializedCohort = null;
    // merge cohort definition with the save modal name & description
    const cohort = Cohort.parseCohort({
      ...this.state.cohort.serialize(),
      ...saveParams,
    });

    const isCreate = this.helpers.isNewSavedCohort() || isDuplicate;

    try {
      if (isCreate) {
        savedCohort = await cohortsClient.createCohort({
          workspaceId: this.attr(`workspace-id`),
          projectId: this.attr(`project-id`),
          cohort: cohort.serialize(),
        });
      } else {
        savedCohort = await cohortsClient.updateCohortById(
          {
            workspaceId: this.attr(`workspace-id`),
            projectId: this.attr(`project-id`),
            cohortID: cohort.id,
          },
          cohort.serialize(),
        );
      }
      const {id} = savedCohort;
      cohortsStore.update({[id]: {...savedCohort, count: this.state.cohortCount}});
      this.dispatchEvent(
        new CustomEvent(`commitSavedCohort`, {
          detail: {
            id,
            name: saveParams.name,
            savedAsNew: isDuplicate,
            cohort: Cohort.parseCohort({
              ...savedCohort,
              count: this.state.cohortCount,
              /** preserve unsavedId in case a temp cohort is saved */
              unsavedId: this.state.cohort.unsavedId,
            }),
          },
        } as InlineCohortsModalCommitEvent),
      );
    } catch {
      const message = isCreate
        ? this.t(`users.cohorts.error.failed-create`)
        : this.t(`users.cohorts.error.failed-save`);
      addToast({
        type: ToastType.ERROR,
        message,
      });
      throw new Error(message);
    }

    return savedCohort;
  }

  async fetchCohortCount() {
    if (!this.attr(`show-cohort-count`)) {
      return;
    }

    this.update({loadingCohortCount: true});
    const statsResponse = await fetchPeopleCountByCohort(this.state.cohort, true, this.attr(`data-group-id`), {
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
    const cohortCount = statsResponse.results;

    if (Number.isInteger(cohortCount)) {
      const samplingFactor = statsResponse.metadata?.min_sampling_factor;
      this.update({
        cohortCount,
        loadingCohortCount: false,
        samplingFactor: samplingFactor ? samplingFactor : 1.0,
      });
    }
  }

  hasUnsavedChanges() {
    if (!this.attr(`cohort`) || !this.state.cohort) {
      return false;
    }
    const canApplyChanges = this.attr(`can-edit`) || !this.state.cohort.id;
    return canApplyChanges && !Cohort.parseCohort(this.attr(`cohort`)).isEqual(this.state.cohort);
  }

  isSamplingTextEnabled() {
    try {
      const currentProject = this.getContext(`currentProject`)?.get()?.value;
      if (!currentProject) {
        return false;
      }
      return currentProject.hasFeatureFlag(ProjectFeatureFlag.SamplingInfo);
    } catch {
      return false;
    }
  }

  private hasExistingName(): boolean {
    const isExistingName = this.helpers.getCohortNames().includes(this.state.name.toLowerCase());
    // if you've changed your name, make sure that you didn't change it to an existing one
    if (this.state.name !== this.attr(`name`)) {
      return isExistingName;
      // if you haven't changed your name and are a new cohort, make sure you didn't change it to an existing one
    } else if (!this.helpers.isNewUnsavedCohort()) {
      return isExistingName;
      // if you haven't changed your name and are an existing cohort, you're fine
    } else if (!this.helpers.isNewCohort()) {
      return false;
    } else {
      return isExistingName;
    }
  }

  private getErrorCopy() {
    if (this.state.errors.has(CohortsModalError.NameRequired)) {
      return this.t(`users.cohorts.error.empty-name`);
    } else if (this.state.errors.has(CohortsModalError.ExistingName)) {
      return this.t(`users.cohorts.error.name-exists`);
    }
    return ``;
  }

  private addError(value: CohortsModalError) {
    const errors = new Set([...this.state.errors]);
    errors.add(value);

    this.update({errors});
  }

  private removeError(value: CohortsModalError) {
    const errors = new Set([...this.state.errors]);
    errors.delete(value);

    this.update({errors});
  }

  private trackEvent(eventName, props) {
    this.dispatchEvent(
      new CustomEvent(`track`, {
        bubbles: true,
        composed: true,
        detail: {
          eventName,
          props,
        },
      }),
    );
  }
}

// TODO: config reads attr, need to tackle this before doing react migration
export const InlineCohortsModalEl = defineAndMakeJsxFactory<InlineCohortsModalAttrsJsxProps>(
  `mp-inline-cohorts-modal`,
  InlineCohortsModal,
);
